import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, CheckCircle2, XCircle, Users } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { UserHeader } from '../../components/UserHeader';
import {
  FormularioFamiliar, RespuestaResumen, respuestaVacia, validarRespuesta, payloadRespuesta,
} from '../../components/actualizacion-familiar/FormularioFamiliar';

const API = process.env.REACT_APP_BACKEND_URL + '/api/actualizacion-familiar';

const Aviso = ({ ok, titulo, texto, testid }) => (
  <div className={`rounded-xl border p-6 flex gap-4 ${ok ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`} data-testid={testid}>
    {ok ? <CheckCircle2 className="h-6 w-6 text-green-600 flex-shrink-0" /> : <XCircle className="h-6 w-6 text-red-600 flex-shrink-0" />}
    <div>
      <p className={`font-semibold ${ok ? 'text-green-800' : 'text-red-800'}`}>{titulo}</p>
      {texto && <p className={`mt-1 text-sm ${ok ? 'text-green-700' : 'text-red-700'}`}>{texto}</p>}
    </div>
  </div>
);

export default function ActualizacionFamiliarPage() {
  const { token } = useParams();
  const [estado, setEstado] = useState(null);
  const [error, setError] = useState('');
  const [form, setForm] = useState(respuestaVacia());
  const [enviando, setEnviando] = useState(false);
  const [errorForm, setErrorForm] = useState('');

  useEffect(() => {
    axios.get(`${API}/mi-estado/${token}`, { timeout: 60000 })
      .then((r) => setEstado(r.data))
      .catch((e) => setError(e.response?.data?.detail || 'No se pudo cargar el formulario.'));
  }, [token]);

  const enviar = async () => {
    const msg = validarRespuesta(form);
    if (msg) return setErrorForm(msg);
    setErrorForm('');
    setEnviando(true);
    try {
      const r = await axios.post(`${API}/responder/${token}`, payloadRespuesta(form), { timeout: 60000 });
      setEstado({ ...estado, completado: true, respuesta: r.data.respuesta, recien: true });
      window.scrollTo(0, 0);
    } catch (e) {
      setErrorForm(e.response?.data?.detail || 'No se pudo enviar. Intentá de nuevo.');
    } finally {
      setEnviando(false);
    }
  };

  let contenido;
  if (error) contenido = <Aviso titulo={error} testid="af-error" />;
  else if (!estado) contenido = (
    <div className="flex items-center gap-3 text-gray-600" data-testid="af-cargando">
      <Loader2 className="h-5 w-5 animate-spin" /> Verificando tu estado de matrícula…
    </div>
  );
  else if (estado.completado) contenido = (
    <div className="space-y-6">
      <Aviso ok testid="af-completado"
        titulo={estado.recien ? '¡Gracias! Tus respuestas quedaron registradas.' : 'Ya completaste este formulario.'}
        texto="Si cometiste un error, comunicate con la administración del Colegio para corregirlo." />
      <div className="rounded-xl border bg-white p-6"><RespuestaResumen respuesta={estado.respuesta} /></div>
    </div>
  );
  else if (!estado.al_dia) contenido = (
    <Aviso testid="af-no-al-dia" titulo="No podés completar el formulario"
      texto={estado.mensaje || 'Para completar este formulario tenés que estar al día con la matrícula.'} />
  );
  else contenido = (
    <div className="rounded-xl border bg-white p-6 sm:p-8 space-y-8" data-testid="af-formulario">
      <FormularioFamiliar value={form} onChange={setForm} />
      {errorForm && <p className="text-sm text-red-600" data-testid="af-error-form">{errorForm}</p>}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 border-t pt-6">
        <Button onClick={enviar} disabled={enviando} className="rounded-full px-8" data-testid="af-enviar-btn">
          {enviando && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Enviar respuestas
        </Button>
        <p className="text-xs text-gray-500">Una vez enviado no se puede modificar.</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <UserHeader />
      <main className="max-w-2xl mx-auto px-4 py-10 space-y-6">
        <div className="flex items-start gap-3">
          <div className="rounded-full bg-primary/10 p-3"><Users className="h-6 w-6 text-primary" /></div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Actualización Familiar</h1>
            <p className="text-sm text-gray-600 mt-1">
              {estado?.nombre ? `Hola ${estado.nombre}. ` : ''}Contanos sobre tu grupo familiar para planificar acciones del Colegio en 2027.
            </p>
          </div>
        </div>
        {contenido}
      </main>
    </div>
  );
}
